import { useState } from 'react';
import FormInput from './FormInput';
import { useAuth } from '../../context/AuthContext';
import styles from './LoginForm.module.css';

const EMAIL_RE = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;

/**
 * 인증 실패는 항상 같은 문구로 돌려준다.
 * 서버 메시지를 그대로 노출하면 "가입되지 않은 이메일" 같은 응답으로
 * 계정 존재 여부를 확인할 수 있게 된다(계정 열거).
 * 다만 자격증명과 무관한 오류(요청 과다·서버 장애·네트워크)는 그대로 알려줘야
 * 사용자가 비밀번호를 계속 다시 치는 상황을 막을 수 있다.
 */
function loginErrorMessage(err) {
  const status = err.response?.status;
  if (!err.response) return '네트워크 연결을 확인해주세요.';
  if (status === 429) return '로그인 시도가 너무 많아요. 잠시 후 다시 시도해주세요.';
  if (status >= 500) return '일시적인 오류가 발생했어요. 잠시 후 다시 시도해주세요.';
  return '이메일 또는 비밀번호를 확인해주세요.';
}

/**
 * 로그인 폼.
 * /login 페이지와 로그인 모달이 함께 쓴다 — 검증·에러 처리가 두 벌이 되지 않도록
 * 성공 후 무엇을 할지(`onSuccess`)만 쓰는 쪽에서 정한다.
 */
export default function LoginForm({ onSuccess, submitLabel = '로그인' }) {
  const { login } = useAuth();

  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  const [error, setError] = useState('');
  const [loading, setLoading] = useState(false);
  const [emailTouched, setEmailTouched] = useState(false);

  // 형식 오류는 입력창 아래에서 필드별로 알린다.
  // 한 번 벗어난 뒤부터 검사해, 타이핑 도중에 빨간 글씨가 뜨지 않게 한다.
  const emailInvalid = emailTouched && email.trim().length > 0 && !EMAIL_RE.test(email.trim());
  const isActive = email.trim().length > 0 && password.trim().length > 0;

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!isActive || loading) return;
    // 형식이 틀린 채로 요청을 보내면 '이메일 또는 비밀번호를 확인해주세요'만 돌아와
    // 무엇이 잘못됐는지 알 수 없다. 보내기 전에 필드에서 짚어준다.
    if (!EMAIL_RE.test(email.trim())) { setEmailTouched(true); return; }
    setError('');
    setLoading(true);
    try {
      await login(email, password);
      onSuccess?.();
    } catch (err) {
      setError(loginErrorMessage(err));
    } finally {
      setLoading(false);
    }
  };

  return (
    <form className={styles.form} onSubmit={handleSubmit}>
      <FormInput
        label="이메일 주소"
        type="email"
        name="email"
        autoComplete="email"
        placeholder="이메일을 입력해주세요"
        value={email}
        onChange={(e) => { setEmail(e.target.value); if (error) setError(''); }}
        onBlur={() => setEmailTouched(true)}
        error={emailInvalid ? '이메일 형식을 확인해주세요' : ''}
      />
      <FormInput
        label="비밀번호"
        type="password"
        name="password"
        autoComplete="current-password"
        placeholder="비밀번호를 입력해주세요"
        value={password}
        onChange={(e) => { setPassword(e.target.value); if (error) setError(''); }}
      />

      {error && <p className={styles.formError} role="alert">{error}</p>}

      <button
        type="submit"
        className={`${styles.submitBtn} ${isActive ? styles.active : ''}`}
        disabled={!isActive || loading}
      >
        {loading ? '로그인 중...' : submitLabel}
      </button>
    </form>
  );
}
